import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // ONE button chrome (Max, 2026-07-18): the outline style — the /sources
        // "Manage domains" look — is THE app-wide button. `default` and `outline`
        // render identically; the teal-filled primary is BANNED in product UI
        // (marketing CTAs on the website keep their own component). Prominence
        // comes from placement (rule 17's toolbar slot), not fill color.
        default:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:shadow-sm active:scale-[0.98]",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 hover:shadow-md active:scale-[0.98]",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:shadow-sm active:scale-[0.98]",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        // 26px = the ViewToggle track height (text-xs leading-none segment +
        // py-1 + p-0.5 + border). Buttons and toggles share toolbar rows, so
        // they share ONE height — same look and feel, color is the only
        // differentiator (Max, 2026-07-18).
        default: "h-[26px] px-3 text-xs",
        sm: "h-[26px] px-2.5 text-xs",
        lg: "h-9 px-5 text-sm",
        icon: "h-[26px] w-[26px] min-touch",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /**
   * Required for icon-only buttons (size="icon") to provide accessible name.
   * Screen readers and a11y tree parsers need this to identify the button's purpose.
   */
  "aria-label"?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
